import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Modal, alert } from 'equasion-lib';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { useRootStore } from '@/lib/providers/RootProvider';
import {
  STALE_PICK_CLAIM_MINUTES,
  pickVerifyPickerName,
  pickVerifyRouteFor,
  pickVerifyStageLabel,
  pickVerifyStepFor
} from '@/lib/pickVerifyShip';
import ReprintStickersButton from '@/lib/shared-coms/ReprintStickersButton';
import CertificateOfOriginButton from '@/lib/shared-coms/CertificateOfOriginButton';
import type { PrintableOrderDocument } from '@/lib/rxPrint';
import downloadFile from '@/lib/utils/downloadFile';
import ShippingBundlePanel from './com/ShippingBundlePanel';
import './SalesOrderSummary.scss';

/** Keep in sync with `DEA_FULFILL_CONTROLLED_SCHEDULES` in libs/db fda-product.model (fulfill-controlled gate). */
const DEA_FULFILL_CONTROLLED_SCHEDULES = ['CV', 'CIV', 'CIII', 'CII', 'CI'];

function SalesOrderSummary({
  salesOrder,
  bundleContext,
  bundleSetAside,
  bundleAddOrder,
  bundleRemoveOrder,
  returnToPicking,
  reopenVerify,
  refetch,
  // RxPrint on: the Verify documents print from here; off, the buttons say Download (@/lib/rxPrint).
  rxPrintEnabled = false,
  // The Verify-time box documents (SUMMARY_PACK_DOCUMENTS) — only asked for a verified order.
  packDocuments = null as any,
  sendingKey = null as string | null,
  onSendToPrinter = undefined as
    | undefined
    | ((key: string, documents: PrintableOrderDocument[]) => void)
}) {
  const navigate = useNavigate();
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const getPermission = useRootStore((state) => state.getPermission);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [sendBackConfirm, setSendBackConfirm] = useState(false);
  const [sendBackBusy, setSendBackBusy] = useState(false);
  // A second tap can land before the re-render disables the button.
  const sendBackBusyRef = useRef(false);
  const {
    id,
    vendorSalesOrderNumber,
    vendorStatus,
    shipsToPuertoRico,
    orderItems = [],
    client
  } = salesOrder || {};
  const { companyName } = client || {};
  const clientName = companyName || 'This client';
  const totalQuantity = orderItems.reduce((a, { quantity }) => a + quantity, 0);
  const titledStatus =
    (vendorStatus &&
      (vendorStatus === 'delayed_ship'
        ? 'Backordered'
        : vendorStatus === 'delayed_ship_draft'
          ? 'B/O Queue'
          : vendorStatus.charAt(0).toUpperCase() + vendorStatus.slice(1))) ||
    '';
  // const pickedPartially = vendorStatus === 'picking';
  const pickedPartially = false;
  const scheduledSalesOrderItems = orderItems.filter(({ product }) => {
    const { fdaProduct, upcProduct } = product || {};
    return fdaProduct?.deaSchedule !== 'OTC' && fdaProduct?.deaSchedule !== 'Rx Exempt' && !upcProduct;
  });
  const unserializedSalesOrderItems = orderItems.filter(({ product }) => {
    const { fdaProduct, upcProduct } = product || {};
    return fdaProduct?.deaSchedule === 'OTC' || fdaProduct?.deaSchedule === 'Rx Exempt' || upcProduct;
  });

  const hasControlledProducts = orderItems.some(({ product }) => {
    const { fdaProduct, upcProduct } = product || {};
    return (
      !upcProduct && DEA_FULFILL_CONTROLLED_SCHEDULES.includes(fdaProduct?.deaSchedule)
    );
  });
  const canFulfillControlled = !!getPermission('sales-orders.fulfill:create');

  const actionColorMap = {
    picking: 'var(--o600)',
    shipping: 'var(--n300)'
  };

  // Pick → Verify → Ship (Option 2), from this order's own resolved workflow. For a standard order
  // the step is null, so every use below falls through to the untouched Option-1 expression
  // (`/sales-order-${vendorStatus}/:id`, "Start Picking"…).
  const pickVerifyStep = pickVerifyStepFor(salesOrder);
  const pickVerifyRoute = pickVerifyRouteFor(salesOrder);
  const pickVerifyAtPicking = pickVerifyStep === 'pick' || pickVerifyStep === 'verify';
  // Controlled products without "Fulfill Controlled Sales Order". The standard workflow keeps the
  // whole order closed to this user. Pick → Verify → Ship opens it to everyone and locks only its
  // controlled lines (the server enforces both), so it gets a note instead of a disabled button.
  const lacksControlled =
    vendorStatus === 'picking' && hasControlledProducts && !canFulfillControlled;
  const pickingBlocked = lacksControlled && !pickVerifyAtPicking;
  const controlledLinesLocked = lacksControlled && pickVerifyAtPicking;
  const pickerName = pickVerifyAtPicking ? pickVerifyPickerName(salesOrder) : null;
  const { started: pickStarted, pickerClaimStale } = salesOrder?.fulfillmentWorkflow || {};
  // Spread rather than an `onClick` attribute: equasion's ButtonProps type omits onClick (it
  // reaches the <button> through ...rest at runtime), and a spread is not excess-property checked.
  const pickVerifyClick = {
    onClick: () => {
      if (pickVerifyRoute) navigate(pickVerifyRoute);
    }
  };

  const runBundleAction = async (
    action,
    { successTitle, successText, thenNavigate }
  ) => {
    if (bundleBusy) return;
    setBundleBusy(true);
    try {
      await action();
      alert(successTitle, successText, 4, 'success');
      if (thenNavigate) navigate(thenNavigate);
      else await refetch();
    } catch (e) {
      const message =
        e?.graphQLErrors?.[0]?.message || e?.message || 'Could not update the shipping bundle.';
      alert('Bundle', message, 6, 'error');
    } finally {
      setBundleBusy(false);
    }
  };

  const handleSetAside = () =>
    runBundleAction(
      () => bundleSetAside({ variables: { companyId: vendorId, salesOrderId: id } }),
      {
        successTitle: 'Set aside',
        successText: `SO #${vendorSalesOrderNumber} is waiting for ${clientName}'s other orders.`,
        thenNavigate: '/sales-orders?tab=shipping'
      }
    );

  const handleJoin = (shippingBundleId) =>
    runBundleAction(
      () =>
        bundleAddOrder({
          variables: { companyId: vendorId, shippingBundleId, salesOrderId: id }
        }),
      {
        successTitle: 'Added to bundle',
        successText: `SO #${vendorSalesOrderNumber} will ship with ${clientName}'s other orders.`,
        thenNavigate: '/sales-orders?tab=shipping'
      }
    );

  const handleRemove = () =>
    runBundleAction(
      () => bundleRemoveOrder({ variables: { companyId: vendorId, salesOrderId: id } }),
      {
        successTitle: 'Removed from bundle',
        successText: `SO #${vendorSalesOrderNumber} will ship on its own.`
      }
    );

  const handleRemoveOther = (order) =>
    runBundleAction(
      () => bundleRemoveOrder({ variables: { companyId: vendorId, salesOrderId: order.id } }),
      {
        successTitle: 'Removed from bundle',
        successText: `SO #${order.vendorSalesOrderNumber} will ship on its own.`
      }
    );

  // A bundled order must stay at shipping for its whole life in the bundle. The operator removes
  // it first (the panel above); the server refuses the same case on both send-back paths.
  const inShippingBundle = !!bundleContext?.bundle;
  const sendBackToVerify = pickVerifyStep === 'documents';

  // The box documents Verify printed, in print order; any not sent to RxPrint is downloaded.
  const orderNumber = vendorSalesOrderNumber || 'order';
  const verifyDocuments = (
    [
      [packDocuments?.packPackingListDocument, `Packing List - SO ${orderNumber}.pdf`],
      [packDocuments?.packInvoiceDocument, `Invoice - SO ${orderNumber}.pdf`],
      [packDocuments?.packStickersDocument, `stickers-${orderNumber}.zpl`]
    ] as [any, string][]
  )
    .filter(([document]) => document?.id && document?.link)
    .map(([document, fallbackFilename]) => ({
      id: document.id,
      download: () => void downloadFile(document.link, document.humanFilename || fallbackFilename)
    }));
  const canReprintVerifyDocuments =
    pickVerifyStep === 'documents' && rxPrintEnabled && !!onSendToPrinter && verifyDocuments.length > 0;

  const handleSendBackToPicking = async () => {
    if (sendBackBusyRef.current || bundleBusy || inShippingBundle) return;
    sendBackBusyRef.current = true;
    setSendBackBusy(true);
    try {
      try {
        if (sendBackToVerify) {
          await reopenVerify({ variables: { companyId: vendorId, salesOrderId: id } });
        } else {
          await returnToPicking({
            variables: {
              orderId: id,
              orderAttributes: { vendorStatus: 'picking' },
              companyId: vendorId
            }
          });
        }
      } catch (e) {
        const message =
          e?.graphQLErrors?.[0]?.message ||
          e?.message ||
          'Could not send this order back to picking.';
        alert('Back to picking', message, 6, 'error');
        return;
      }
      setSendBackConfirm(false);
      alert(
        'Back to picking',
        sendBackToVerify
          ? `SO #${vendorSalesOrderNumber} left shipping and is ready to verify again.`
          : `SO #${vendorSalesOrderNumber} is back in picking.`,
        4,
        'success'
      );
      try {
        await refetch();
      } catch {
        alert(
          'Refresh',
          `SO #${vendorSalesOrderNumber} was sent back, but this page could not reload.`,
          6,
          'error'
        );
      }
    } finally {
      sendBackBusyRef.current = false;
      setSendBackBusy(false);
    }
  };

  return (
    <Page className="sales-order-summary action-page">
      <ActionTopBar
        action={pickVerifyAtPicking ? pickVerifyStageLabel(pickVerifyStep) : titledStatus}
        backPath={
          pickVerifyStep === 'verify'
            ? '/sales-orders?tab=verify'
            : vendorStatus === 'shipping'
              ? '/sales-orders?tab=shipping'
              : '/sales-orders'
        }
        actionColor={actionColorMap[vendorStatus]}
      />
      <div className="action-page-body">
        {pickedPartially && <i>Partially {pickedPartially && 'Picked'}</i>}
        <h1>SO #{vendorSalesOrderNumber}</h1>
        <h2>
          {totalQuantity} Items - {companyName}
        </h2>

        {vendorStatus === 'shipping' && (
          <ShippingBundlePanel
            context={bundleContext}
            currentOrder={salesOrder}
            clientName={clientName}
            busy={bundleBusy}
            onSetAside={handleSetAside}
            onJoin={handleJoin}
            onRemove={handleRemove}
            onRemoveOther={handleRemoveOther}
          />
        )}

        <div className="header-row">
          <div className="row-header">Item({orderItems.length})</div>
          {/* <div className="row-header">Priority Location</div> */}
        </div>
        {scheduledSalesOrderItems.map(({ id: salesOrderItemId, product }) => (
          <div className="data-row" key={salesOrderItemId}>
            <div className="row-data">
              <WarehouseProductDisplay product={product} />
            </div>
          </div>
        ))}
        {!!unserializedSalesOrderItems.length && (
          <div className="otc-separator">Unserialized Products</div>
        )}
        {unserializedSalesOrderItems.map(({ id: salesOrderItemId, product }) => (
          <div className="data-row" key={salesOrderItemId}>
            <div className="row-data">
              <WarehouseProductDisplay product={product} />
            </div>
          </div>
        ))}

        {pickingBlocked && (
          <p style={{ color: 'var(--red)', textAlign: 'center', padding: '8px 0' }}>
            This order contains controlled products. You need the "Fulfill Controlled
            Sales Order" permission to pick.
          </p>
        )}
        {controlledLinesLocked && (
          <p className="pick-verify-controlled-note">
            {pickVerifyStep === 'verify'
              ? 'This order contains controlled products. You can verify its other products; a user with the "Fulfill Controlled Sales Order" permission verifies the controlled ones.'
              : 'This order contains controlled products. You can pick its other products; a picker with the "Fulfill Controlled Sales Order" permission picks the controlled ones.'}
          </p>
        )}
        {!!pickerName && (
          <p className="pick-verify-picker">
            Picking: {pickerName}
            {pickerClaimStale && ` · inactive ${STALE_PICK_CLAIM_MINUTES}+ min`}
          </p>
        )}
        {/* Verified and waiting to ship: Ship itself is unchanged (the primary button below). With
            RxPrint on, the box documents printed at the end of Verify print again from here in one
            tap; the second opens them on the Verify screen, one by one (it only navigates, so its
            label says so rather than Print / Download). */}
        {canReprintVerifyDocuments && (
          <Button
            type="secondary"
            fullWidth
            className="pick-verify-documents-reprint document-print-button"
            disabled={!!sendingKey}
            {...{ onClick: () => onSendToPrinter('verify-documents', verifyDocuments) }}
          >
            {sendingKey === 'verify-documents' ? 'Sending…' : 'Re-print Verify Documents'}
          </Button>
        )}
        {pickVerifyStep === 'documents' && (
          <Button
            type="secondary"
            fullWidth
            className="pick-verify-documents document-print-button"
            {...pickVerifyClick}
          >
            Open Verify Documents
          </Button>
        )}
        {pickVerifyAtPicking && (
          <Button
            type="primary"
            fullWidth
            disabled={pickingBlocked}
            {...pickVerifyClick}
          >
            {pickVerifyStep === 'verify' ? 'Start Verify' : `${pickStarted ? 'Resume' : 'Start'} Pick`}
          </Button>
        )}
        {!pickVerifyAtPicking && vendorStatus !== 'completed' && (
          <Button
            type="primary"
            fullWidth
            disabled={pickingBlocked}
            onClick={() => navigate(`/sales-order-${vendorStatus}/${id}`)}
          >
            {pickedPartially ? 'Resume' : 'Start'} {titledStatus}
          </Button>
        )}
        {vendorStatus === 'shipping' && (
          <Button
            type="secondary"
            fullWidth
            className="send-back-to-picking"
            disabled={bundleBusy || sendBackBusy}
            {...{ onClick: () => setSendBackConfirm(true) }}
          >
            Send Back to Picking
          </Button>
        )}
        {vendorStatus === 'shipping' && (
          <ReprintStickersButton
            orderId={id}
            vendorSalesOrderNumber={vendorSalesOrderNumber}
            vendorStatus={vendorStatus}
            rxPrintEnabled={rxPrintEnabled}
          />
        )}
        {/* Puerto Rico: the Certificate of Origin travels with the shipment. Renders nothing for
            any other order. */}
        {vendorStatus === 'shipping' && (
          <CertificateOfOriginButton
            orderId={id}
            vendorStatus={vendorStatus}
            shipsToPuertoRico={shipsToPuertoRico}
            rxPrintEnabled={rxPrintEnabled}
            sendingKey={sendingKey}
            onSendToPrinter={onSendToPrinter}
          />
        )}
      </div>
      <Modal
        className="send-back-to-picking-modal"
        modalOpen={sendBackConfirm}
        setModalOpen={(open) => {
          if (!open && !sendBackBusyRef.current) setSendBackConfirm(false);
        }}
      >
        <div className="send-back-to-picking-modal__body">
          <strong>Send back to picking?</strong>
          <p>
            {inShippingBundle
              ? 'Remove this order from its shipping bundle before sending it back to picking.'
              : sendBackToVerify
                ? `SO #${vendorSalesOrderNumber} leaves the shipping queue and returns to Verify.`
                : `SO #${vendorSalesOrderNumber} leaves the shipping queue.`}
          </p>
          <div>
            {!inShippingBundle && (
              <Button
                type="primary"
                fullWidth
                disabled={sendBackBusy}
                {...{ onClick: handleSendBackToPicking }}
              >
                {sendBackBusy ? 'Sending…' : 'Send Back to Picking'}
              </Button>
            )}
            <Button
              type="secondary"
              fullWidth
              disabled={sendBackBusy}
              {...{ onClick: () => setSendBackConfirm(false) }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
}

export default SalesOrderSummary;
